(() => {
  if (window.ThemeSwiperUtils) return;

  const REGISTERED_KEYS = new Set();

  const findNearestSectionElement = (element) =>
    element?.closest?.('section[id^="shopify-section-"]') ||
    element?.closest?.('[id^="shopify-section-"]') ||
    null;

  const parseNumberAttribute = (element, attributeName, fallbackValue) => {
    const rawValue = element?.getAttribute?.(attributeName);
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
  };

  const whenSwiperIsAvailable = (callback, { maxFrames = 120 } = {}) => {
    let frames = 0;

    const tick = () => {
      frames += 1;

      if (window.Swiper) {
        callback();
        return;
      }

      if (frames >= maxFrames) return;
      requestAnimationFrame(tick);
    };

    tick();
  };

  const registerSectionSlider = (config) => {
    if (!config || typeof config !== 'object') return;
    const key = String(config.key || '').trim();
    if (!key) return;

    if (REGISTERED_KEYS.has(key)) return;
    REGISTERED_KEYS.add(key);

    const selectors = config.selectors || {};
    const instancesBySectionId = new Map();

    const getSectionId = (sectionElement, sliderElement) =>
      sectionElement?.id ||
      sliderElement?.getAttribute?.('data-section-id') ||
      `${key}-${Math.random().toString(16).slice(2)}`;

    const update = (sliderElement, swiperInstance) => {
      try {
        config.onUpdate?.({ sliderElement, swiperInstance });
      } catch (_) {}
    };

    const createSwiperInstance = (sliderElement) => {
      const swiperContainerElement = sliderElement.querySelector(selectors.swiperContainer);
      if (!swiperContainerElement || !window.Swiper) return null;

      const previousButtonElement = selectors.previousButton
        ? sliderElement.querySelector(selectors.previousButton)
        : null;

      const nextButtonElement = selectors.nextButton
        ? sliderElement.querySelector(selectors.nextButton)
        : null;

      const paginationContainerElement = selectors.paginationContainer
        ? sliderElement.querySelector(selectors.paginationContainer)
        : null;

      const spaceBetweenSlides = parseNumberAttribute(
        sliderElement,
        config.dataAttributes?.spaceBetween || 'data-space-between',
        24
      );
      const transitionSpeed = parseNumberAttribute(
        sliderElement,
        config.dataAttributes?.speed || 'data-speed',
        350
      );

      const options = (config.getSwiperOptions?.({
        sliderElement,
        swiperContainerElement,
        previousButtonElement,
        nextButtonElement,
        paginationContainerElement,
        spaceBetweenSlides,
        transitionSpeed,
      })) || {};

      // Guarantee common defaults if caller didn't provide them
      if (typeof options !== 'object' || options === null) return null;

      return new window.Swiper(swiperContainerElement, options);
    };

    const destroySectionInstance = (sectionElement) => {
      const sectionId = sectionElement?.id;
      if (!sectionId) return;

      const instance = instancesBySectionId.get(sectionId);
      if (!instance) return;

      try {
        window.removeEventListener('resize', instance.resizeHandler);
      } catch (_) {}

      try {
        instance.swiperInstance?.destroy?.(true, true);
      } catch (_) {}

      instancesBySectionId.delete(sectionId);
    };

    const initializeSectionInstance = (sectionElement) => {
      if (!sectionElement) return;

      const sliderElement = sectionElement.querySelector(selectors.sliderContainer);
      if (!sliderElement) return;

      const sectionId = getSectionId(sectionElement, sliderElement);
      if (instancesBySectionId.has(sectionId)) return;

      if (!window.Swiper) return;

      const swiperInstance = createSwiperInstance(sliderElement);
      if (!swiperInstance) return;

      const resizeHandler = () => {
        try {
          swiperInstance.update?.();
        } catch (_) {}
      };

      if (config.watchResize !== false) {
        window.addEventListener('resize', resizeHandler, { passive: true });
      }

      // Ensure update runs at least once
      update(sliderElement, swiperInstance);

      // Keep updates consistent across sections
      try {
        swiperInstance.on?.('slideChange', () => update(sliderElement, swiperInstance));
      } catch (_) {}

      instancesBySectionId.set(sectionId, { swiperInstance, resizeHandler });
    };

    const initializeAllInstances = () => {
      const sliderElements = Array.from(document.querySelectorAll(selectors.sliderContainer));
      sliderElements.forEach((sliderElement) => {
        const sectionElement = findNearestSectionElement(sliderElement);
        if (!sectionElement) return;
        initializeSectionInstance(sectionElement);
      });
    };

    const initialize = () => whenSwiperIsAvailable(initializeAllInstances);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }

    document.addEventListener('shopify:section:load', (event) => {
      initializeSectionInstance(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroySectionInstance(event.target);
    });

    document.addEventListener('shopify:section:select', (event) => {
      const sectionId = event.target?.id;
      const instance = sectionId ? instancesBySectionId.get(sectionId) : null;
      try {
        instance?.swiperInstance?.update?.();
      } catch (_) {}
    });
  };

  window.ThemeSwiperUtils = {
    registerSectionSlider,
    findNearestSectionElement,
    parseNumberAttribute,
    whenSwiperIsAvailable,
  };
})();
